import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
// COYN-1188: import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FC, ReactNode, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppTheme, ExtendedTypographyOptions } from "../../utils/theme";
import SkeletonLoader from "../../components/SkeletonLoader";
import { useUserStore } from "../../hooks/useUserStore";
import { usePageStore } from "@/hooks/usePageStore";
import { useLocation } from "react-router-dom"; // COYN-1188: removed useNavigate
import { useGetSettings } from "@/hooks/useSetting";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { MODULE_NAME, OPTIONS, ACTION_CODE } from "@/domain/auditlog.enum";
// COYN-1188: import Menu from "@mui/material/Menu";
// COYN-1188: import MenuItem from "@mui/material/MenuItem";
// COYN-1182: import Button from "@mui/material/Button";
// COYN-1188: import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs from "dayjs";
import { DrawerMenuContainer } from "@/components/DrawerMenu";
import { useGetCurrentAppVersion } from "@/hooks/useAppVersions";
import { AppVersionPlatformEnum } from "@/domain/appVersion.interface";
import GenericAlertModal from "@/components/AlertModal";
import { Grid, Divider } from "@mui/material";

const drawerWidth = 240;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  minHeight: "calc(100vh - 82px)",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2.5),
  marginTop: "82px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(1.5),
  },
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  height: "82px",
  display: "flex",
  flexDirection: "row",
  backgroundColor: "#fff",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    marginLeft: 0,
  },
}));

type LayoutProps = {
  children: ReactNode;
};

const Layout: FC<LayoutProps> = ({ children }: LayoutProps) => {
  const theme = useAppTheme();
  const { isLoading, userInfo } = useUserStore();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(!isMobile);
  const { pageTitle } = usePageStore();
  // COYN-1188: const navigate = useNavigate();
  const { data: settings } = useGetSettings();
  const [showVersionModal, setShowVersionModal] = useState(false);
  const location = useLocation();
  // Fetch current backoffice app version
  const { appVersion: currentVersion } = useGetCurrentAppVersion(
    AppVersionPlatformEnum.BACKOFFICE,
  );

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  // COYN-1188: const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (isMobile && open) {
      setOpen(false);
    } else if (!isMobile && !open) {
      setOpen(true);
    }
  }, [isMobile]);

  const { logout } = useAuth0();
  const createAuditLog = useCreateAuditlog();

  const signOutFunction = async () => {
    try {
      await createAuditLog.mutateAsync({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.SIGN_OUT,
        option: OPTIONS.SIGN_OUT,
        actionCode: ACTION_CODE.WEB_SIGN_OUT_OK,
        action: "Sign out",
        detail: "User signed out successfully",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
    } finally {
      document.cookie = "";
      sessionStorage.clear();
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };

  /* COYN-1188: Hidden user menu handlers
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  */

  const isParticipantRoute = React.useMemo(() => {
    return !!location?.pathname?.startsWith("/participants");
  }, [location]);

  const isParticipantListRoute = React.useMemo(() => {
    return location?.pathname === "/participants";
  }, [location]);

  useEffect(() => {
    const inactivityLimit = settings?.inactivityTimeout
      ? Number(settings.inactivityTimeout || 0) * 1000 // Convert seconds to milliseconds
      : 15 * 60 * 1000; // Default to 15 minutes
    let lastActivityTime = Date.now();

    const updateLastActivity = () => {
      lastActivityTime = Date.now();
    };

    const checkInactivity = () => {
      if (Date.now() - lastActivityTime > inactivityLimit) {
        signOutFunction();
      }
    };

    // List of events to detect user activity
    const activityEvents = ["mousemove", "keydown", "click", "scroll"];

    // Attach event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, updateLastActivity);
    });

    // Set up interval to check for inactivity
    const intervalId = setInterval(checkInactivity, 5 * 1000); // Check every 5 seconds

    // Cleanup function
    return () => {
      clearInterval(intervalId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, updateLastActivity);
      });
    };
  }, [logout, settings?.inactivityTimeout]);

  useEffect(() => {
    if (settings?.country === "US") {
      dayjs.locale("en");
    } else if (settings?.country === "UK") {
      dayjs.locale("en-gb");
    }
  }, [settings?.country]);

  return (
    <Box
      sx={{ display: "flex", backgroundColor: theme.palette.surface.main }}
      data-testid="layout"
    >
      <CssBaseline />
      <AppBar position="fixed" open={open} className="flex">
        <Toolbar className="flex-1 gap-4">
          <IconButton
            size="large"
            edge="start"
            aria-label="menu"
            data-testid="drawer-button"
            onClick={() => (open ? handleDrawerClose() : handleDrawerOpen())}
          >
            <MenuIcon />
          </IconButton>
          {!isParticipantRoute ? (
            <>
              {/* COYN-1182: Hidden search bar and register participant button from all screens
              {userHasAccessToCode("PARTICIPANTS_VIEW") && (
                <ParticipantSearchBar />
              )}
              {userHasAccessToCode("PARTICIPANT_ADD") && (
                <Button
                  size="large"
                  onClick={() => setShowRegisterModal(true)}
                  color="primary"
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    height: "3rem",
                    textAlign: "center",
                  }}
                >
                  REGISTER PARTICIPANT
                </Button>
              )}
              */}
            </>
          ) : isParticipantListRoute ? (
            <div className="flex flex-1 rounded-lg px-1 py-3 max-w-xl items-center justify-between h-12">
              <Typography
                color={theme.palette.textMain.dark2}
                fontWeight={700}
                fontSize={
                  (theme.typography as ExtendedTypographyOptions).titleSmall
                    ?.fontSize
                }
              >
                {pageTitle}
              </Typography>
            </div>
          ) : null}
        </Toolbar>
        {/* COYN-1188: Removed Tooltip, arrow icon, and dropdown menu. Aligned name/role to the right. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: 2,
          }}
          data-testid="user-button"
        >
          <div className="flex flex-col items-end">
            <span className="text-base font-bold text-text-main">
              {`${userInfo?.firstName} ${userInfo?.lastName}`}
            </span>
            <span className="text-xs text-text-main2">
              {userInfo?.roles?.length &&
                userInfo?.roles[0] &&
                userInfo?.roles[0].name}
            </span>
          </div>
        </Box>
        {/* COYN-1188: Hidden dropdown menu - Support page not implemented
        <Tooltip title="Open settings">
          <Button
            size="large"
            onClick={handleOpenUserMenu}
            sx={{
              textTransform: "none",
              justifyContent: "space-between",
              width: "232px",
            }}
            data-testid="user-button"
          >
            <div className="flex items-center gap-4 w-full">
              <div className="flex flex-col flex-1 justify-start items-start">
                <span className="text-base font-bold text-text-main">
                  {`${userInfo?.firstName} ${userInfo?.lastName}`}
                </span>
                <span className="text-xs text-text-main2">
                  {userInfo?.roles?.length &&
                    userInfo?.roles[0] &&
                    userInfo?.roles[0].name}
                </span>
              </div>
              <KeyboardArrowDownIcon className="text-dark" />
            </div>
          </Button>
        </Tooltip>
        <Menu
          sx={{ mt: "45px" }}
          id="menu-appbar"
          anchorEl={anchorElUser}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          data-testid="user-menu"
        >
          <MenuItem
            onClick={() => {
              navigate("/helpsupport");
            }}
            data-testid="menu-item"
          >
            <Typography textAlign="center">Support</Typography>
          </MenuItem>
        </Menu>
        */}
      </AppBar>

      {/* Drawer Menu - Using COYN architecture */}
      <DrawerMenuContainer
        isOpen={open}
        onClose={handleDrawerClose}
        isMobile={isMobile}
        appVersion={{
          functionalVersion:
            currentVersion?.functionalVersion || "v1.0 build 1",
        }}
        onVersionClick={() => setShowVersionModal(true)}
      />

      <Main open={open}>
        <SkeletonLoader isLoading={isLoading} skeletonCount={1}>
          <Box sx={{ flexGrow: 1 }}>{children}</Box>
        </SkeletonLoader>
      </Main>
      {/* COYN-1182: Hidden register participant modal
      {showRegisterModal ? (
        <ParticipantRegisterModal
          open={showRegisterModal}
          onClose={() => {
            setShowRegisterModal(false);
          }}
        />
      ) : null}
      */}

      {/* Version Modal */}
      {showVersionModal && currentVersion && (
        <GenericAlertModal
          theme={theme}
          onCancel={() => setShowVersionModal(false)}
          title="Version Information"
          description={
            <Grid container spacing={2} sx={{ mt: 1, minWidth: 300 }}>
              <Grid item xs={6}>
                <Typography variant="bodyMedium" fontWeight={600}>
                  Component
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="bodyMedium" fontWeight={600}>
                  Version
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="bodyMedium">Web</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="bodyMedium">
                  {currentVersion.version}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="bodyMedium">Backend</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="bodyMedium">
                  {currentVersion.apiVersion}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="bodyMedium">Functional</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="bodyMedium">
                  {currentVersion.functionalVersion}
                </Typography>
              </Grid>
            </Grid>
          }
          cancelText="Close"
          showFotter={true}
        />
      )}
    </Box>
  );
};

export default Layout;
